/**
 * Bold, italic and links for a form field, nothing else.
 *
 * The textarea underneath stays the field; the editor writes back into it on
 * every change, so validation, posting and the export still read a string, and
 * a visitor without this script keeps a working textarea. `formats` is the
 * whole vocabulary Quill may produce and what it filters a paste down to — a
 * pasted Word document being the normal case here. None of it is a security
 * measure; the server cleans what arrives.
 */

import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const FORMATS = ['bold', 'italic', 'link'];

function initField(container: HTMLElement): void {
    const surface = container.querySelector('[data-rich-text-surface]') as HTMLElement;
    const wrapper = container.closest('.FormTextbox--RichText') as HTMLElement;
    const value = wrapper && wrapper.querySelector('textarea') as HTMLTextAreaElement;
    if (!surface || !value) return;

    container.hidden = false;

    const editor = new Quill(surface, {
        theme: 'snow',
        formats: FORMATS,
        placeholder: value.getAttribute('placeholder') || '',
        modules: {
            toolbar: [['bold', 'italic', 'link']]
        }
    });

    // A visitor lands on the editable area, so the label and help text have to
    // describe it rather than the textarea it replaced.
    const label = wrapper.querySelector('.Form__Element__Caption');
    const description = wrapper.querySelector('.Form__Element__Description');
    if (label && label.id) editor.root.setAttribute('aria-labelledby', label.id);
    if (description && description.id) editor.root.setAttribute('aria-describedby', description.id);

    // Existing content goes in through the paste filter, so a value from a
    // failed post cannot smuggle markup back in.
    if (value.value) {
        editor.clipboard.dangerouslyPasteHTML(value.value);
    }

    value.classList.add('FormRichText__Value--replaced');
    value.setAttribute('tabindex', '-1');
    value.setAttribute('aria-hidden', 'true');

    function sync(): void {
        // An empty editor still holds a paragraph; the field must read empty
        // for a required validator to behave.
        value.value = editor.getLength() > 1 ? editor.getSemanticHTML() : '';
    }

    editor.on('text-change', sync);
    sync();
}

Array.prototype.slice.call(document.querySelectorAll('[data-rich-text]'))
    .forEach((container: HTMLElement) => initField(container));
